////
import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Delete as DeleteIcon,
  Google,
  Microsoft,
  Apple,
  CalendarMonth as CalendarIcon,
} from '@mui/icons-material';
import { useMutation } from '@tanstack/react-query';
import { useAuthStore } from '../../stores/auth.store';
import { useCalendarManagement } from '../../hooks/useCalendarManagement';
import { useCalendarOAuthRedirect } from '../../hooks/useCalendarOAuthRedirect';
import { useSearchParams } from 'react-router-dom';
import {
  CalendarDomain,
  CalendarType,
  IUserCalendar,
} from '../../@shared/interfaces/calendar.interfaces';
import { theme as appTheme } from '../../theme/ThemeProvider';
import { toast } from 'react-hot-toast';

// Simplified styled components
const CalendarCard = styled(Card)(() => ({
  backgroundColor: appTheme.colors.black2,
  borderRadius: '8px',
  marginBottom: '12px',
  boxShadow: 'none',
  border: `1px solid ${appTheme.colors.input.border}`,
  '.MuiCardContent-root': {
    padding: '16px',
  },
}));

const ProviderIcon = styled(Box)(() => ({
  width: 32,
  height: 32,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '4px',
  backgroundColor: 'transparent',
  '& svg': {
    color: 'inherit',
  },
}));

const LabelText = styled(Typography)(() => ({
  fontSize: '12px',
  color: appTheme.colors.white,
  marginBottom: '4px',
}));

// const ValueText = styled(Typography)(() => ({
//   fontSize: '14px',
//   color: appTheme.colors.white,
//   fontWeight: 500,
// }));

const ConnectButton = styled(Button)(() => ({
  borderRadius: '6px',
  textTransform: 'none',
  padding: '8px 16px',
  fontSize: '14px',
}));

const StyledChip = styled(Chip)(() => ({
  borderRadius: '4px',
  height: '24px',
  fontSize: '12px',
  fontWeight: 500,
}));

// Add this styled component near the top with other styled components
const StyledInput = styled('input')(() => ({
  fontSize: '12px',
  fontWeight: 500,
  color: appTheme.colors.white,
  background: `linear-gradient(225deg, ${appTheme.colors.primary} 0%, ${appTheme.colors.secondary} 50%, rgba(255, 255, 255, 0.1) 100%)`,
  border: 'none',
  borderRadius: '4px',
  padding: '4px 8px',
  height: '24px',
  minWidth: '80px',
  '&::placeholder': {
    color: 'rgba(255, 255, 255, 0.5)',
  },
  '&:focus': {
    outline: 'none',
    opacity: 0.9,
  },
}));

// Calendar domain configuration with icons and colors
const CalendarProviderConfig = {
  [CalendarDomain.GOOGLE]: {
    icon: <Google fontSize="small" />,
    color: appTheme.colors.info,
    name: 'Google',
  },
  [CalendarDomain.OUTLOOK]: {
    icon: <Microsoft fontSize="small" />,
    color: appTheme.colors.info,
    name: 'Outlook',
  },
  [CalendarDomain.APPLE]: {
    icon: <Apple fontSize="small" />,
    color: appTheme.colors.info,
    name: 'Apple',
  },
  default: {
    icon: <CalendarIcon fontSize="small" />,
    color: appTheme.colors.info,
    name: 'Calendar',
  },
};

interface CalendarManagementProps {
  _onCalendarAdded?: () => void;
  onCalendarRemoved?: () => void;
  onCalendarUpdated?: () => void;
}

export const CalendarManagement: React.FC<CalendarManagementProps> = ({
  onCalendarRemoved,
  onCalendarUpdated,
}) => {
  const profile = useAuthStore((state: any) => state.profile);
  const fetchProfile = useAuthStore((state) => state.fetchProfile);
  const { getGoogleOAuthUrl, getOutlookOAuthUrl, disconnectCalendar } =
    useCalendarManagement();
  const [searchParams] = useSearchParams();
  const [calendarToRemove, setCalendarToRemove] = useState<string | null>(null);
  const [labelInputs, setLabelInputs] = useState<{ [key: string]: string }>({});
  const isMobile = useMediaQuery('(max-width:600px)');

  // Use the unified OAuth redirect handler
  useCalendarOAuthRedirect(profile?._id);

  const updateCalendarMutation = useMutation({
    mutationFn: async (updatedCalendars: IUserCalendar[]) => {
      // TODO: Implement API call to update calendars
      return updatedCalendars;
    },
    onSuccess: async () => {
      // Refresh profile data from Zustand store
      await fetchProfile();
      onCalendarUpdated?.();
    },
  });

  const _handleUpdateType = (calendarId: string, type: CalendarType) => {
    if (!profile?.settings.calendars) return;

    const updatedCalendars = profile.settings.calendars.map(
      (calendar: IUserCalendar) =>
        calendar.id === calendarId ? { ...calendar, type } : calendar
    );

    updateCalendarMutation.mutate(updatedCalendars);
  };

  const _handleUpdateTags = (calendarId: string, tags: string[]) => {
    if (!profile?.settings.calendars) return;

    const updatedCalendars = profile.settings.calendars.map(
      (calendar: IUserCalendar) =>
        calendar.id === calendarId ? { ...calendar, tags } : calendar
    );

    updateCalendarMutation.mutate(updatedCalendars);
  };

  const handleRemoveCalendar = (calendarId: string) => {
    const calendar = profile?.settings.calendars?.find(
      (cal: IUserCalendar) => cal.id === calendarId
    );

    if (!calendar) {
      toast.error('Calendar not found');
      return;
    }

    // Use the unified disconnect function
    disconnectCalendar.mutate(
      { calendarId: calendar.id, domain: calendar.domain },
      {
        onSuccess: () => {
          onCalendarRemoved?.();
        },
      }
    );
  };

  const handleConnectGoogleCalendar = async () => {
    try {
      const state = searchParams.toString();
      const authUrl = await getGoogleOAuthUrl.mutateAsync();
      window.location.href = `${authUrl}&state=${encodeURIComponent(state)}`;
    } catch (error) {
      console.error('Failed to get Google OAuth URL:', error);
      toast.error('Failed to start Google Calendar connection');
    }
  };

  const handleConnectOutlookCalendar = async () => {
    try {
      const authUrl = await getOutlookOAuthUrl.mutateAsync({
        state: CalendarDomain.OUTLOOK,
      });
      window.location.href = authUrl;
    } catch (error) {
      console.error('Failed to get Outlook OAuth URL:', error);
      toast.error('Failed to start Outlook Calendar connection');
    }
  };

  // Helper function to get the provider config
  const getProviderConfig = (domain: CalendarDomain) => {
    return CalendarProviderConfig[domain] || CalendarProviderConfig.default;
  };

  const connectedCalendars = profile?.settings.calendars || [];
  const hasConnectedCalendars = connectedCalendars.length > 0;

  return (
    <Box>
      {hasConnectedCalendars ? (
        <Stack spacing={1}>
          {connectedCalendars
            .sort((a: IUserCalendar, b: IUserCalendar) =>
              b.isPrimary ? 1 : 0 - (a.isPrimary ? 1 : 0)
            )
            .map((calendar: IUserCalendar) => {
              const providerConfig = getProviderConfig(calendar.domain);
              return (
                <CalendarCard key={calendar.id}>
                  <CardContent>
                    <Box
                      sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                    >
                      {/* Header Section */}
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: { xs: 'column', sm: 'row' },
                          gap: { xs: 1.5, sm: 0 },
                        }}
                      >
                        {/* Primary Tag - Mobile Only */}
                        {calendar.isPrimary && (
                          <Box
                            sx={{
                              display: { xs: 'flex', sm: 'none' },
                              justifyContent: 'flex-start',
                            }}
                          >
                            <StyledChip
                              label="Primary"
                              size="small"
                              sx={{
                                background: `linear-gradient(90deg, ${appTheme.colors.primary} 0%, ${appTheme.colors.secondary} 100%)`,
                                color: appTheme.colors.white,
                              }}
                            />
                          </Box>
                        )}

                        {/* Email and Provider Section */}
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            width: '100%',
                            ml: 0,
                            pl: 0,
                            justifyContent: 'flex-start',
                          }}
                        >
                          <ProviderIcon
                            sx={{ ml: 0, pl: 0, color: providerConfig.color }}
                          >
                            {providerConfig.icon}
                          </ProviderIcon>

                          <Typography
                            sx={{
                              ml: 0,
                              flex: 1,
                              fontSize: '14px',
                              fontWeight: 500,
                              color: appTheme.colors.white,
                            }}
                          >
                            {calendar.email}
                          </Typography>

                          {/* Primary Tag - Web Only */}
                          {calendar.isPrimary && (
                            <Box
                              sx={{
                                display: { xs: 'none', sm: 'flex' },
                                ml: 1,
                              }}
                            >
                              <StyledChip
                                label="Primary"
                                size="small"
                                sx={{
                                  background: `linear-gradient(90deg, ${appTheme.colors.primary} 0%, ${appTheme.colors.secondary} 100%)`,
                                  color: appTheme.colors.white,
                                }}
                              />
                            </Box>
                          )}
                        </Box>
                      </Box>

                      {/* Type and Label Section */}
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 2,
                        }}
                      >
                        <Box>
                          <LabelText>Type</LabelText>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            {(
                              ['personal', 'work', 'other'] as CalendarType[]
                            ).map((type) => (
                              <StyledChip
                                key={type}
                                label={type}
                                size="small"
                                onClick={() =>
                                  _handleUpdateType(calendar.id, type)
                                }
                                sx={{
                                  background:
                                    calendar.type === type
                                      ? `linear-gradient(225deg, ${appTheme.colors.primary} 0%, ${appTheme.colors.secondary} 50%)`
                                      : appTheme.colors.black,
                                  color: appTheme.colors.white,
                                  border: `1px solid ${calendar.type === type ? 'transparent' : appTheme.colors.input.border}`,
                                  cursor: 'pointer',
                                  '&:hover': {
                                    opacity: calendar.type === type ? 0.9 : 1,
                                    backgroundColor:
                                      calendar.type === type
                                        ? 'transparent'
                                        : appTheme.colors.black2,
                                  },
                                }}
                              />
                            ))}
                          </Box>
                        </Box>

                        <Box>
                          <LabelText>Label</LabelText>
                          <StyledInput
                            type="text"
                            value={
                              labelInputs[calendar.id] ??
                              calendar.tags?.[0] ??
                              ''
                            }
                            placeholder="Add label"
                            onChange={(e) => {
                              setLabelInputs((prev) => ({
                                ...prev,
                                [calendar.id]: e.target.value,
                              }));
                            }}
                            onBlur={(e) => {
                              const trimmedValue = e.target.value.trim();
                              _handleUpdateTags(
                                calendar.id,
                                trimmedValue ? [trimmedValue] : []
                              );
                              setLabelInputs((prev) => ({
                                ...prev,
                                [calendar.id]: trimmedValue,
                              }));
                            }}
                          />
                        </Box>
                      </Box>

                      {/* Delete Button */}
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'flex-end',
                        }}
                      >
                        <IconButton
                          sx={{
                            color: '#ef4444',
                            p: 0.5,
                            '&:hover': {
                              backgroundColor: 'rgba(239, 68, 68, 0.1)',
                            },
                          }}
                          onClick={() => setCalendarToRemove(calendar.id)}
                          size="small"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  </CardContent>
                </CalendarCard>
              );
            })}
        </Stack>
      ) : (
        <Box
          sx={{
            textAlign: 'center',
            py: 3,
            px: 2,
            backgroundColor: 'transparent',
            mb: 3,
          }}
        >
          <Typography
            variant="body1"
            sx={{ color: appTheme.colors.white, mb: 1 }}
          >
            No calendars connected
          </Typography>
          <Typography variant="body2" sx={{ color: appTheme.colors.white }}>
            Connect your calendar for scheduling
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap: 2,
          mt: 3,
          justifyContent: 'center',
        }}
      >
        <ConnectButton
          variant="outlined"
          startIcon={<Google />}
          onClick={handleConnectGoogleCalendar}
          sx={{
            borderColor: appTheme.colors.info,
            color: appTheme.colors.white,
            '&:hover': {
              borderColor: appTheme.colors.primary,
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
            },
          }}
        >
          ADD GOOGLE CALENDAR
        </ConnectButton>
        <ConnectButton
          variant="outlined"
          startIcon={<Microsoft />}
          onClick={handleConnectOutlookCalendar}
          sx={{
            borderColor: appTheme.colors.info,
            color: appTheme.colors.white,
            '&:hover': {
              borderColor: appTheme.colors.primary,
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
            },
          }}
        >
          ADD OUTLOOK CALENDAR
        </ConnectButton>
      </Box>

      <Dialog
        open={!!calendarToRemove}
        onClose={() => setCalendarToRemove(null)}
        PaperProps={{
          sx: {
            backgroundColor: appTheme.colors.black2,
            color: appTheme.colors.text.primary,
            borderRadius: '8px',
          },
        }}
      >
        <DialogTitle>Remove Calendar</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: appTheme.colors.white }}>
            Are you sure you want to remove this calendar?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCalendarToRemove(null)}>Cancel</Button>
          <Button
            onClick={() => {
              if (calendarToRemove) {
                handleRemoveCalendar(calendarToRemove);
                setCalendarToRemove(null);
              }
            }}
            color="error"
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CalendarManagement;
